import { useState } from 'react';
import { t } from '@hana/i18n';
import '../strings.i18n';
import { useNavigate } from '@tanstack/react-router';
import { IconArrowLeft, IconBell, IconEye, IconLayoutSidebarRight, IconList, IconPlus, IconSparkles } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState, type Tone } from '@hana/ui';
import { useWide } from '../useWide';
import { BUILDS, LANGS, LANG_K, RANGES, TABS, attention, overview, type Lang, type RangeKey, type TabKey, type Tn } from './webadmin';
import s from './webadmin.module.css';

// Mint-WebAdmin (1536 / 1920): Quản trị website · tab Tổng quan. Các tab còn lại dựng ở bước sau; chỉ đọc, nút ghi báo toast.

const TONE: Record<Tn, string> = { ok: s.tOk!, warn: s.tWarn!, err: s.tErr!, pri: s.tPri!, mute: s.tMute! };
const BUILD_DOT = { ok: 'var(--ok)', err: 'var(--err)', run: 'var(--warn)' } as const;
const NAV_BADGE: Partial<Record<TabKey, { n: number; tone: Tone }>> = { landing: { n: 1, tone: 'err' }, news: { n: 1, tone: 'info' } };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function WebAdmin() {
  const toast = useToast();
  const navigate = useNavigate();
  const wide = useWide();
  const forced = readState();
  const q = new URLSearchParams(location.search);
  const canPub = q.get('role') !== 'edit';
  const state: ScreenState = forced ?? 'ready';
  const [tab, setTab] = useState<TabKey>('overview');
  const [range, setRange] = useState<RangeKey>('30');
  const [lang, setLang] = useState<Lang>('');
  const [dense, setDense] = useState(false);
  const [right, setRight] = useState(false);
  const soon = (what: string) => () => toast({ msg: `${what} chưa mở ở bản này` });
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const ov = overview(range, lang, false);
  const last = BUILDS[0]!;
  const dockOpen = wide || right;
  const showDock = dockOpen && state !== 'noperm' && state !== 'loading' && state !== 'error';
  const rowH = dense ? 40 : 48;

  const tabItems = TABS.map(([key, label]) => {
    const b = NAV_BADGE[key];
    return { key, label: b ? <>{label}<span className={`${s.nb} ${b.tone === 'err' ? s.nbErr : s.nbInfo}`}>{b.n}</span></> : label };
  });

  const body = () => {
    if (tab !== 'overview') return <div className={s.later}>{t('kit.x.tabNayChua')}</div>;
    if (ov.empty) return null;
    return (
      <div className={s.ov}>
        <div className={s.kpis}>
          {ov.kpis.map((k) => (
            <div key={k.k} className={s.kpi}>
              <div className={s.kLb}>{k.k}</div>
              <div className={s.kVal}>{k.v}</div>
              <div className={s.kSub}><span className={`${s.delta} ${TONE[k.tone]}`}>{k.d}</span>{k.s}</div>
            </div>
          ))}
        </div>
        <div className={s.row2}>
          <section className={s.box}>
            <div className={s.bHead}><b>{t('kit.x.khachVaBao')}</b><div className={s.leg}><span><i className={s.legLine} />{t('kit.x.khach')}</span><span><i className={s.legSq} />{t('kit.x.baoGia')}</span></div></div>
            <div className={s.chart}>
              <div className={s.bars}>{ov.bars.map((b, i) => <div key={i} title={b.tip} style={{ height: `${b.h}%` }} />)}</div>
              <svg aria-hidden="true" viewBox="0 0 1000 172" preserveAspectRatio="none" className={s.line}><polyline points={ov.pts} fill="none" stroke="var(--pri)" strokeWidth="2.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" /></svg>
              <div className={s.xl}>{ov.xl.map((x, i) => <span key={i}>{x}</span>)}</div>
            </div>
          </section>
          <section className={`${s.box} ${s.fun}`}>
            <b>{t('kit.x.pheuKhach')}</b>
            {ov.funnel.map((f) => (
              <div key={f.k} className={s.fRow}>
                <div className={s.fTop}><span>{f.k}</span><span><b>{f.n}</b><span className={s.lb}> · {f.c}</span></span></div>
                <div className={s.fTrack}><div style={{ width: `${f.pct}%`, background: f.col }} /></div>
              </div>
            ))}
          </section>
        </div>
        <div className={s.row3}>
          {ov.cards.map((g) => (
            <section key={g.key} className={`${s.box} ${s.card}`}>
              <div className={s.cHead}><b>{g.k}</b><button type="button" className={s.lnk} onClick={() => setTab('traffic')}><IconEye size={14} aria-hidden="true" />{t('kit.x.xemChiTiet')}</button></div>
              <div className={s.cCols}><span>{g.h1}</span><span className={s.r}>{g.h2}</span><span className={s.r}>{t('kit.x.baoGia')}</span></div>
              {g.rows.map((r) => (
                <div key={r.k} className={s.cRow}>
                  <div className={s.cName}><div>{r.k}</div><div className={s.cTrack}><div style={{ width: `${r.pct}%` }} /></div></div>
                  <span className={`${s.r} ${s.b}`}>{r.n}</span><span className={`${s.r} ${s.tx2}`}>{r.q}</span>
                </div>
              ))}
              {g.foot && <div className={s.lb}>{g.foot}</div>}
            </section>
          ))}
        </div>
        <section className={`${s.box} ${s.lps}`}>
          <div className={s.lHead}><b>{t('kit.x.landingHieuQua')}</b><button type="button" className={s.lnk} onClick={() => setTab('landing')}><IconList size={14} aria-hidden="true" />{t('kit.x.moiLanding')}</button></div>
          <div className={`${s.lRow} ${s.lTh}`} role="row">
            <span role="columnheader">Trang</span><span role="columnheader">{t('kit.x.nguonChinh')}</span><span role="columnheader" className={s.r}>{t('kit.x.khach')}</span><span role="columnheader" className={s.r}>{t('kit.x.baoGia')}</span><span role="columnheader" className={s.r}>{t('kit.x.chuyenDoi')}</span><span role="columnheader" className={s.r}>Won</span>
          </div>
          {ov.landings.map((p) => (
            <div key={p.id} className={s.lRow} role="row" style={{ height: rowH }} onClick={soon('Trình dựng landing')}>
              <span role="cell" className={s.lT}><span className={s.b}>{p.title}</span><span className={s.path}>{p.path}</span></span>
              <span role="cell" className={s.tx2}>{p.src}</span><span role="cell" className={`${s.r} ${s.b}`}>{p.vis}</span><span role="cell" className={s.r}>{p.q}</span><span role="cell" className={s.r}>{p.c}</span><span role="cell" className={s.r}>{p.won}</span>
            </div>
          ))}
        </section>
      </div>
    );
  };

  const dock = (
    <aside className={s.dock}>
      <b className={s.dkT}>{t('kit.x.canChuY')}</b>
      {attention(canPub).map((m) => (
        <div key={m.tag} className={s.att}>
          <div className={s.attTop}><span className={`${s.tag} ${TONE[m.tone]}`}>{m.tag}</span><span className={s.lb} style={{ marginLeft: 'auto' }}>{m.when}</span></div>
          <b className={s.b}>{m.k}</b>
          <div className={s.lb}>{m.s}</div>
          <button type="button" className={s.attBtn} onClick={soon(m.btn)}>{m.btn}</button>
        </div>
      ))}
      <a className={s.rfq} href="/ezy/marketing?tab=rfq"><span><IconSparkles size={16} aria-hidden="true" />{t('kit.x.phieuHoiGia')}</span><span aria-hidden="true">→</span></a>
      <b className={s.bd}>{t('kit.x.buildGanDay')}</b>
      {BUILDS.slice(0, 4).map((b) => (
        <button key={b.id} type="button" className={s.build} onClick={soon('Log build')}>
          <span className={s.bDot} style={{ background: BUILD_DOT[b.s] }} />
          <span><span className={s.bK}>{b.k}</span><span className={s.lb}>{b.at} · {b.by} · {b.d}</span></span>
        </button>
      ))}
    </aside>
  );

  return (
    <Page kind="table" dock={wide && showDock ? dock : undefined}>
      <PageHeader
        title="Website"
        lead={<button type="button" className={s.back} aria-label="Về Quản trị hệ thống" title="Về Quản trị hệ thống" onClick={() => void navigate({ to: '/kit' } as never)}><IconArrowLeft size={18} stroke={1.9} aria-hidden="true" /></button>}
        actions={
          <>
            <button type="button" className={s.build2} title="Lịch sử build" onClick={soon('Lịch sử build')}><i style={{ background: BUILD_DOT[last.s] }} />Build xong {last.at.slice(-5)}</button>
            <HeaderTool label="Thông báo · 3 mới" icon={IconBell} n={3} onClick={soon('Thông báo')} />
            <Button variant="ask" icon={<IconSparkles size={16} aria-hidden="true" />} onClick={soon('Ask HANA')}>Ask HANA</Button>
            {!wide && <HeaderTool label="Cột phải: cần chú ý" icon={IconLayoutSidebarRight} active={right} onClick={() => setRight((o) => !o)} />}
            {canPub && <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('kit.x.taoLanding')}</Button>}
          </>
        }
      />
      <div className={!wide && dockOpen && state !== 'noperm' ? s.split : undefined}>
        <div className={s.page}>
          <div className={s.tabs}>
            <Segmented label="Phân mục website" value={tab} onChange={setTab} items={tabItems} />
            <span style={{ flexGrow: 1 }} />
            {(tab === 'overview' || tab === 'traffic') && (
              <>
                <Segmented kind="toggle" label="Khoảng ngày" value={range} onChange={setRange} items={RANGES.map(([key, label]) => ({ key, label }))} />
                <div className={s.lang}>
                  <span>{t('kit.x.ngonNgu')}</span>
                  <select value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
                    <option value="">{t('kit.x.tatCa')}</option>
                    {LANGS.map((l) => <option key={l} value={l}>{l} · {LANG_K[l]}</option>)}
                  </select>
                </div>
              </>
            )}
            <Segmented kind="toggle" label="Mật độ" value={dense ? 'g' : 'c'} onChange={(v) => setDense(v === 'g')} items={[{ key: 'c', label: 'Chuẩn' }, { key: 'g', label: 'Gọn' }]} />
          </div>
          <section className={s.region} aria-label="Website">
            <StateView
              state={state === 'ready' && ov.empty ? 'empty' : state}
              title={state === 'noperm' ? 'Website cần quyền form WebAdmin' : state === 'error' ? 'Không đọc được dữ liệu website' : undefined}
              action={state === 'error' ? <Button onClick={() => location.reload()}>{t('kit.x.thuLai')}</Button> : undefined}
              skeleton={<Skeleton rows={8} />}
            >
              {body()}
            </StateView>
          </section>
        </div>
        {!wide && dockOpen && showDock && dock}
      </div>
    </Page>
  );
}
